// 인증 관련 유틸리티 함수들

/**
 * 현재 사용 가능한 토큰을 반환 (JWT 토큰 우선)
 * @returns {string|null} 토큰 문자열 또는 null
 */
export const getCurrentToken = () => {
  // JWT 토큰이 있으면 우선 사용
  const jwtToken = localStorage.getItem('jwtToken');
  if (jwtToken) {
    return jwtToken;
  }
  
  // JWT 토큰이 없으면 기존 AuthContext 토큰 사용
  const authToken = localStorage.getItem('auth_token');
  return authToken || null;
};

/**
 * Authorization 헤더가 포함된 요청 헤더를 생성
 * @param {Object} additionalHeaders - 추가할 헤더들
 * @returns {Object} 완성된 헤더 객체
 */
export const getAuthHeaders = (additionalHeaders = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...additionalHeaders
  };
  
  const token = getCurrentToken();
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    console.log('🔑 [AuthHelper] Authorization 헤더 설정:', {
      tokenSource: localStorage.getItem('jwtToken') ? 'JWT' : 'AuthContext',
      tokenLength: token.length,
      tokenStart: token.substring(0, 20) + '...'
    });
  } else {
    console.warn('⚠️ [AuthHelper] 인증 토큰이 없습니다.');
  }
  
  return headers;
};

/**
 * 모든 인증 관련 데이터를 localStorage에서 삭제
 */
export const clearAuthData = () => {
  // JWT 토큰 관련
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('user');
  
  // AuthContext 토큰 관련
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_info');
  
  console.log('🧹 [AuthHelper] 모든 인증 데이터 삭제 완료');
};

/**
 * 현재 사용자가 인증된 상태인지 확인
 * @returns {boolean} 인증 상태
 */
export const isAuthenticated = () => {
  return !!getCurrentToken();
};

/**
 * 현재 사용자 정보를 반환 (JWT 우선)
 * @returns {Object|null} 사용자 정보 객체 또는 null
 */
export const getCurrentUser = () => {
  try {
    // JWT 사용자 정보가 있으면 우선 사용
    const jwtUser = localStorage.getItem('user');
    if (jwtUser) {
      return JSON.parse(jwtUser);
    }
    
    // JWT 사용자 정보가 없으면 기존 AuthContext 사용자 정보 사용
    const authUser = localStorage.getItem('user_info');
    return authUser ? JSON.parse(authUser) : null;
  } catch (error) {
    console.error('❌ [AuthHelper] 사용자 정보 파싱 오류:', error);
    return null;
  }
};

/**
 * 401 에러 처리 (자동 로그아웃)
 * @param {Response} response - fetch 응답 객체
 * @returns {boolean} 401 에러였는지 여부
 */
export const handleUnauthorizedError = (response) => {
  if (response.status === 401) {
    console.warn('⚠️ [AuthHelper] 401 Unauthorized - 자동 로그아웃 처리');
    clearAuthData();
    
    // 페이지 새로고침으로 로그아웃 상태 반영
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
    
    return true;
  }
  
  return false;
};

/**
 * 인증이 필요한 API 요청을 위한 공통 fetch 함수
 * @param {string} url - 요청 URL
 * @param {Object} options - fetch 옵션
 * @returns {Promise<Response>} fetch 응답
 */
export const authenticatedFetch = async (url, options = {}) => {
  const headers = getAuthHeaders(options.headers);
  
  const response = await fetch(url, {
    ...options,
    headers
  });
  
  // 401 에러 자동 처리
  if (handleUnauthorizedError(response)) {
    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
  }
  
  return response;
};